import { NgFor, NgIf, NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { UtilService } from '../../services/util.service';
import { ApiService } from '../../services/api.service';

interface NavItem {
  /** Route segment under /account, or an absolute path when `external`. */
  path: string;
  label: string;
  /** Base file name in /images/svg/account; `-active` is appended for the active state. */
  icon: string;
  isShow: boolean;
  external?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const COLLAPSED_KEY = 'accountNavCollapsed';

/** Paths that draw their own cards. FULL path: recent-order is, its children are not. */
const PLAIN_PATHS = ['', 'overview', 'recent-order', 'quotes', 'wishlist', 'manage-address', 'manage-payment'];

/** Same, for pages whose path carries an id. */
const PLAIN_PREFIXES = ['quotes/view/'];

/** Pages one level under a section: the section crumb turns into a link and these
 *  supply the tail. */
const DETAIL_CRUMBS: { prefix: string; label: string }[] = [
  { prefix: 'quotes/view/', label: 'Quote Details' },
];

@Component({
  selector: 'app-account',
  imports: [NgFor, NgIf, NgTemplateOutlet, RouterOutlet, RouterLink],
  templateUrl: './account.component.html',
  styleUrl: './account.component.css'
})
export class AccountComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  user: any;
  collapsed = false;
  mobileNavOpen = false;
  avatarSrc = '/images/svg/user.svg';

  navGroups: NavGroup[] = [
    {
      label: 'Account',
      items: [
        { path: 'overview', label: 'Overview', icon: 'overview', isShow: true },
        { path: 'recent-order', label: 'My Orders', icon: 'orders', isShow: true },
        { path: 'quotes', label: 'My Quotes', icon: 'quotes', isShow: true },
        { path: 'wishlist', label: 'Saved Items', icon: 'saved-items', isShow: true },
        { path: 'returns', label: 'Returns', icon: 'returns', isShow: true },
      ]
    },
    {
      label: 'Account Settings',
      items: [
        { path: 'profile', label: 'Profile', icon: 'profile', isShow: true },
        { path: 'security', label: 'Security', icon: 'security', isShow: true },
        { path: 'manage-address', label: 'Addresses', icon: 'addresses', isShow: true },
        { path: 'manage-payment', label: 'Payment Methods', icon: 'payment-methods', isShow: true },
        { path: 'manage-team', label: 'Manage Team', icon: 'manage-team', isShow: true },
      ]
    },
    {
      label: 'Support',
      items: [
        { path: '/contact', label: 'Help Center', icon: 'help-center', isShow: true, external: true },
      ]
    },
  ];

  private initialized = false;

  constructor(private router: Router, private util: UtilService, private api: ApiService) { }

  async ngOnInit() {
    if (this.initialized) return;
    this.initialized = true;

    // Route changes close the mobile drawer; active state is read from router.url.
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.closeMobileNav());

    if (isPlatformBrowser(this.platformId)) {
      this.collapsed = localStorage.getItem(COLLAPSED_KEY) === '1';
      const user = await this.api.getCurrentUser();
      this.user = user;
      this.avatarSrc = user?.imageUrl || user?.profileImage || '/images/svg/user.svg';
      this.util.setCurrentUser(user);
      localStorage.setItem('currentUser', JSON.stringify(user));
      this.updateListVisibility();
    }
  }

  get displayName(): string {
    const name = `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim();
    return name || 'My Account';
  }

  onAvatarError() {
    this.avatarSrc = '/images/svg/user.svg';
  }

  /** Users without a customer record only get Security (and Overview, which is where
   *  /account lands) — same rule the previous sidebar applied. */
  private updateListVisibility() {
    const customer = this.user?.customer;
    const noCustomer = !customer ||
      (Array.isArray(customer) && customer.length === 0) ||
      (typeof customer === 'object' && Object.keys(customer).length === 0);

    if (noCustomer) {
      for (const group of this.navGroups) {
        for (const item of group.items) {
          item.isShow = item.path === 'security' || item.path === 'overview';
        }
      }
    }

    document.body.classList.remove('modal-open');
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
    document.querySelectorAll('.modal.show').forEach(modal => modal.classList.remove('show'));
  }

  hasVisibleItems(group: NavGroup): boolean {
    return group.items.some(item => item.isShow);
  }

  /** First path segment under /account, so a sub-route still lights up its rail item. */
  private currentSection(): string {
    const match = this.router.url.split(/[?#]/)[0].match(/\/account\/([^\/]+)/);
    return match ? match[1] : '';
  }

  /** Everything under /account, e.g. "recent-order/view/123". */
  private currentPath(): string {
    const match = this.router.url.split(/[?#]/)[0].match(/\/account\/?(.*)$/);
    return match ? match[1].replace(/\/+$/, '') : '';
  }

  isActive(item: NavItem): boolean {
    return !item.external && this.currentSection() === item.path;
  }

  isPlainPage(): boolean {
    const path = this.currentPath();
    return PLAIN_PATHS.includes(path) || PLAIN_PREFIXES.some(prefix => path.startsWith(prefix));
  }

  /** Empty unless the current page sits below its section. */
  detailCrumbLabel(): string {
    const path = this.currentPath();
    return DETAIL_CRUMBS.find(crumb => path.startsWith(crumb.prefix))?.label || '';
  }

  sectionLink(): string {
    return `/account/${this.currentSection()}`;
  }

  /** The landing page is the only one whose breadcrumb stops at "Account". */
  isOverviewPage(): boolean {
    const path = this.currentPath();
    return path === '' || path === 'overview';
  }

  /** Breadcrumb tail / mobile bar title. */
  currentSectionLabel(): string {
    const section = this.currentSection();
    for (const group of this.navGroups) {
      const hit = group.items.find(item => !item.external && item.path === section);
      if (hit) return hit.label;
    }
    return 'Account';
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(COLLAPSED_KEY, this.collapsed ? '1' : '0');
    }
  }

  openMobileNav() {
    this.mobileNavOpen = true;
    this.markSheetOpen(true);
  }

  closeMobileNav() {
    this.mobileNavOpen = false;
    this.markSheetOpen(false);
  }

  private markSheetOpen(open: boolean) {
    if (!isPlatformBrowser(this.platformId)) return;
    document.body.classList.toggle('account-sheet-open', open);
  }

  logout() {
    localStorage.removeItem('token');
    this.util.setCurrentUser(null);
    this.router.navigateByUrl('/');
    this.util.CartUpdated([]);
  }
}
